import { isFinal, isLive, type Game } from './api';

/**
 * How big a game is, so the home page and the schedule can both lead with it.
 *
 * The old answer was the kickoff clock, which is why "Ranked matchups first" sat
 * above Western Kentucky at New Mexico State: the league page sorted by rank and
 * then grouped by date, and the grouping threw the ranking away.
 *
 * Primetime is deliberately not the signal. A Thursday night game between two
 * 1-3 teams is on national television and nobody wants it, and the biggest game
 * of a week is often a one o'clock kickoff. What actually makes a game worth
 * watching is that both teams are good and nobody knows who wins.
 *
 * The spread carries the injury news for free. A market that opened a game at
 * -3 and moved to -9 has already priced the quarterback being out, so a tight
 * spread between two winning teams is a better read on whether a game is still
 * worth watching than any broadcast slot or any injury list we could fetch.
 */

/** Points from "PIT -2.5", as a positive number. Null when there is no line yet. */
export function spreadOf(game: Game): number | null {
  const m = /-?\d+(\.\d+)?/.exec(game.odds ?? '');
  if (!m) return null;
  const n = Math.abs(Number(m[0]));
  return Number.isFinite(n) ? n : null;
}

/** Win rate from "3-1" or "10-2-1". Null when a team has not played. */
export function winPct(record?: string | null): number | null {
  const m = /^(\d+)-(\d+)(?:-(\d+))?/.exec((record ?? '').trim());
  if (!m) return null;
  const [w, l, t] = [Number(m[1]), Number(m[2]), Number(m[3] ?? 0)];
  const played = w + l + t;
  return played ? (w + t / 2) / played : null;
}

const rank = (g: Game, side: 'home_team' | 'away_team') => g[side].rank ?? null;

/**
 * Bigger is better. The scale has no units; only the ordering matters.
 *
 * College is decided first on ranked against ranked, because that is the whole
 * shape of the sport's week, and among those on how close the two ranks are: 3
 * against 5 is a bigger game than 2 against 24.
 */
export function importance(game: Game): number {
  let score = 0;

  const a = rank(game, 'away_team');
  const h = rank(game, 'home_team');
  const ranked = a !== null && h !== null;
  if (ranked) {
    // Ranked against ranked outranks everything else on the board, and within that
    // tier the ranks decide it: two high ranks beat two low ones, and an even
    // matchup beats a mismatch. #5 against #14 is a bigger game than #2 against #24.
    score += 1000;
    score += Math.max(0, 60 - (a + h)) * 6;
    score += Math.max(0, 25 - Math.abs(a - h)) * 8;
  } else if (a !== null || h !== null) {
    score += 400 + Math.max(0, 30 - (a ?? h ?? 30)) * 4;
  }

  // Both teams winning. Worth the same as a decent single-ranked game, which is
  // what carries the NFL, where nobody is ranked at all.
  const wa = winPct(game.away_team.record);
  const wh = winPct(game.home_team.record);
  if (wa !== null && wh !== null) {
    score += (wa + wh) * 150;          // two good teams
    score += (1 - Math.abs(wa - wh)) * 60;  // of similar quality
  }

  // Nobody knows who wins. A pick'em is worth far more than a three score blowout.
  const spread = spreadOf(game);
  if (spread !== null) score += Math.max(0, 14 - spread) * 8;

  // A tiebreak, not a reason. Two equal games, the one more people will watch.
  if (/prime|snf|mnf|tnf|nbc|espn|abc|fox$|cbs$/i.test(game.broadcast ?? '')) score += 10;

  return score;
}

/** Live first, then whatever is biggest, then the rest. Finals last. */
export function byImportance(games: Game[]): Game[] {
  const phase = (g: Game) => (isLive(g) ? 0 : isFinal(g) ? 2 : 1);
  return [...games].sort(
    (x, y) =>
      phase(x) - phase(y) ||
      importance(y) - importance(x) ||
      new Date(x.date).getTime() - new Date(y.date).getTime(),
  );
}

/** The handful worth putting in front of someone who just arrived. */
export function headline(games: Game[], n = 5): Game[] {
  const ahead = byImportance(games.filter((g) => !isFinal(g)));
  if (ahead.length) return ahead.slice(0, n);
  // Between the last whistle of a week and the feed rolling over, every game is
  // finished. The banner showed nothing at all, which reads as a broken page
  // rather than a quiet Tuesday, so it falls back to what just happened.
  return byImportance(games).slice(0, n);
}

/**
 * The same, but alternating leagues, for a banner that has to speak for both.
 *
 * Scores are not comparable across leagues and were never meant to be. A ranked
 * college matchup starts at 1000 because that is how college weeks are shaped,
 * while the NFL has no ranking at all and tops out near 450. Taking the best five
 * overall therefore handed every slot to college and the NFL never appeared.
 *
 * So each league is ranked on its own and the two lists are zipped together.
 * Whichever league has the bigger game leads, and a league with nothing on
 * simply yields its slots to the other.
 */
export function headlineMixed(games: Game[], n = 5): Game[] {
  const live = (g: Game) => (isLive(g) ? 0 : 1);
  const pick = (lg: 'nfl' | 'cfb') =>
    headline(games.filter((g) => (g.league ?? 'nfl') === lg), n);

  const nfl = pick('nfl');
  const cfb = pick('cfb');
  // whichever league's best game is bigger gets the first slot
  const [a, b] = (cfb[0] ? importance(cfb[0]) : -1) >= (nfl[0] ? importance(nfl[0]) : -1)
    ? [cfb, nfl]
    : [nfl, cfb];

  const out: Game[] = [];
  for (let i = 0; out.length < n && (i < a.length || i < b.length); i++) {
    if (a[i]) out.push(a[i]);
    if (out.length < n && b[i]) out.push(b[i]);
  }
  // a game in progress still outranks anything that has not kicked off
  return out.sort((x, y) => live(x) - live(y)).slice(0, n);
}
